// YouTube videos: recognising their links, and turning a video's captions into
// a transcript the reader can treat as an article.
//
// Pure functions only — no network. The server and the browser extension both
// fetch from YouTube (the server is often turned away as a data-centre address,
// so the extension fetches from the user's own connection instead), and both
// hand what they got to the same code here.
//
// The captions come from YouTube's own app API ("innertube"), as the Android
// app asks for them: the caption links embedded in a watch page are only
// honoured together with a token a real browser computes, while the Android
// client's are plain URLs.

import { decodeEntities, type Chapter, type Cue } from './transcript.ts';

// The transcript itself is built by shared/transcript.ts, as a podcast's is.
export { buildTranscript, formatTime, type Chapter, type Cue, type Transcript } from './transcript.ts';

// ── Links ─────────────────────────────────────────────────────────────────────

const HOSTS = /^(?:www\.|m\.|music\.)?youtube(?:-nocookie)?\.com$/i;
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

/** The video a YouTube link points to, or null for anything else (a channel, a playlist, another site). */
export function youtubeVideoId(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(parsed.protocol)) return null;

  let id: string | null | undefined = null;
  if (/^(?:www\.)?youtu\.be$/i.test(parsed.hostname)) {
    id = parsed.pathname.split('/')[1];
  } else if (HOSTS.test(parsed.hostname)) {
    const [, first, second] = parsed.pathname.split('/');
    if (first === 'watch') id = parsed.searchParams.get('v');
    else if (['shorts', 'live', 'embed', 'v'].includes(first ?? '')) id = second;
  }
  return id && VIDEO_ID.test(id) ? id : null;
}

export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

// ── The player API ────────────────────────────────────────────────────────────

export const PLAYER_ENDPOINT = 'https://www.youtube.com/youtubei/v1/player?prettyPrint=false';

/** The body of a player request, as the Android app sends it. */
export function playerRequestBody(videoId: string): string {
  return JSON.stringify({
    context: { client: { clientName: 'ANDROID', clientVersion: '20.10.38', androidSdkVersion: 34, hl: 'en' } },
    videoId,
  });
}

export interface CaptionTrack {
  baseUrl: string;
  languageCode: string;
  /** 'asr' for YouTube's automatic captions; absent for ones a person wrote. */
  kind?: string;
  name?: string;
}

export interface VideoDetails {
  title: string | null;
  author: string | null;
  lengthSeconds: number | null;
  description: string | null;
}

export type PlayerOutcome =
  | { status: 'ok'; details: VideoDetails; tracks: CaptionTrack[] }
  /** Turned away as a likely bot: worth trying again from somewhere else. */
  | { status: 'blocked'; reason: string }
  /** Private, removed, age-restricted…: no retry will help. */
  | { status: 'unavailable'; reason: string };

type Text = { simpleText?: string; runs?: Array<{ text?: string }> } | undefined;

function textOf(value: Text): string | undefined {
  return value?.simpleText ?? value?.runs?.map((run) => run.text ?? '').join('');
}

/** Make sense of a player response (as parsed JSON). */
export function readPlayer(response: unknown): PlayerOutcome {
  const json = (response ?? {}) as {
    playabilityStatus?: { status?: string; reason?: string };
    videoDetails?: { title?: string; author?: string; lengthSeconds?: string; shortDescription?: string };
    captions?: { playerCaptionsTracklistRenderer?: { captionTracks?: Array<CaptionTrack & { name?: Text }> } };
  };
  const status = json.playabilityStatus?.status;
  const reason = json.playabilityStatus?.reason || status || 'No response from YouTube';
  if (status !== 'OK') {
    // "Sign in to confirm you're not a bot" is about the address asking, not
    // the video; every other refusal is about the video.
    return /\bbot\b/i.test(reason) ? { status: 'blocked', reason } : { status: 'unavailable', reason };
  }

  const video = json.videoDetails ?? {};
  const length = Number(video.lengthSeconds);
  const tracks = (json.captions?.playerCaptionsTracklistRenderer?.captionTracks ?? [])
    .filter((track) => typeof track.baseUrl === 'string' && typeof track.languageCode === 'string')
    .map((track) => ({
      baseUrl: track.baseUrl,
      languageCode: track.languageCode,
      ...(track.kind ? { kind: track.kind } : {}),
      ...(textOf(track.name) ? { name: textOf(track.name) } : {}),
    }));
  return {
    status: 'ok',
    details: {
      title: video.title || null,
      author: video.author || null,
      lengthSeconds: Number.isFinite(length) && length > 0 ? length : null,
      description: video.shortDescription || null,
    },
    tracks,
  };
}

/**
 * Which captions to use: English written by a person, then the automatic
 * captions (which are in whatever language is spoken), then any written by a
 * person. Automatic captions come before a person's in another language
 * because those are usually translations of an English video.
 */
export function pickTrack(tracks: CaptionTrack[]): CaptionTrack | null {
  const written = tracks.filter((track) => track.kind !== 'asr');
  return written.find((track) => /^en\b/i.test(track.languageCode))
    ?? tracks.find((track) => track.kind === 'asr')
    ?? written[0]
    ?? null;
}

// ── Captions ──────────────────────────────────────────────────────────────────

/**
 * Parse YouTube's timed-text XML: "format 3" (`<p t=ms>`, automatic captions
 * splitting each line into `<s>` words) or the older `<text start=s>`. Cues
 * that are only a sound in brackets ("[Music]") are dropped; they are not
 * part of what anyone said.
 */
export function parseTimedText(xml: string): Cue[] {
  const cues: Cue[] = [];
  const add = (start: number, inner: string): void => {
    const text = decodeEntities(inner.replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim();
    if (!text || !Number.isFinite(start) || /^\[[^\]]*\]$/.test(text)) return;
    cues.push({ start, text });
  };

  for (const match of xml.matchAll(/<p\b([^>]*)>([\s\S]*?)<\/p>/g)) {
    const t = /\bt="(\d+)"/.exec(match[1]!);
    if (t) add(Number(t[1]) / 1000, match[2]!);
  }
  if (!cues.length) {
    for (const match of xml.matchAll(/<text\b([^>]*)>([\s\S]*?)<\/text>/g)) {
      const start = /\bstart="([\d.]+)"/.exec(match[1]!);
      if (start) add(Number(start[1]), match[2]!);
    }
  }
  return cues.sort((a, b) => a.start! - b.start!);
}

// ── Chapters ──────────────────────────────────────────────────────────────────

const STAMP = String.raw`(?:\d{1,2}:)?\d{1,2}:\d{2}`;
const STAMP_FIRST = new RegExp(String.raw`^\s*[-•*▶►]?\s*[(\[]?(${STAMP})[)\]]?\s*[-–—:|.]?\s+(.+?)\s*$`);
const STAMP_LAST = new RegExp(String.raw`^\s*[-•*]?\s*(.+?)\s*[-–—:|(\[]?\s*(${STAMP})[)\]]?\s*$`);

function seconds(stamp: string): number {
  return stamp.split(':').reduce((total, part) => total * 60 + Number(part), 0);
}

/**
 * Chapters as YouTube reads them from a description: lines with a timestamp
 * and a title, the first at 0:00, in order. Anything that doesn't look like
 * that list (a stray timestamp in prose) is not taken for one.
 */
export function parseChapters(description: string | null | undefined, lengthSeconds?: number | null): Chapter[] {
  if (!description) return [];
  const found: Chapter[] = [];
  for (const line of description.split('\n')) {
    const first = STAMP_FIRST.exec(line);
    const last = first ? null : STAMP_LAST.exec(line);
    const stamp = first ? first[1]! : last?.[2];
    const title = first ? first[2]! : last?.[1];
    if (!stamp || !title) continue;
    const start = seconds(stamp);
    if (lengthSeconds && start >= lengthSeconds) continue;
    found.push({ start, title: title.replace(/\s+/g, ' ').trim() });
  }
  if (found.length < 2 || found[0]!.start !== 0) return [];
  for (let i = 1; i < found.length; i++) {
    if (found[i]!.start <= found[i - 1]!.start) return [];
  }
  return found;
}
